import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
// @mui
import {
  Grid,
  Card,
  Skeleton,
  CardContent,
  Typography,
  Stack,
  CardHeader,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
// components
import Iconify from '../../components/Iconify';
// swr
import useSWR from 'swr';
// hooks
import useAuth from '../../hooks/useAuth';
// utils
import axiosSWRFetcher from '../../utils/swrFetcher';
import axios from '../../utils/axios';

const IconStyle = styled(Iconify)(({ theme }) => ({
  width: 20,
  height: 20,
  flexShrink: 0,
  marginRight: theme.spacing(1),
}));


export default function SettingsNotification() {
  const { data, isValidating, mutate } = useSWR(`/api/crews/settings/`, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { user, getUser } = useAuth();
  const [offerAllow, setOfferAllow] = useState(!!data?.offer_allow);
  const [allowEmail, setAllowEmail] = useState(!!user?.opt_in_email);
  const [allowSms, setAllowSms] = useState(!!user?.opt_in_sms);

  useEffect(() => {
    if (data?.offer_allow !== undefined) {
      setOfferAllow(data?.offer_allow);
    }
    if (user?.opt_in_email !== undefined) {
      setAllowEmail(!!user?.opt_in_email);
    }
    if (user?.opt_in_sms !== undefined) {
      setAllowSms(!!user?.opt_in_sms);
    }
  }, [data, user]);

  const postOfferAllow = async (value) => {
    try {
      setIsSubmitting(true);
      const postData = {
        ...data,
        username: data.crew?.user_name,
        nickname: data.crew?.nickname,
        offer_allow: value,
      }
      await axios.post(
        `/api/crews/settings/`,
        postData
      );
      enqueueSnackbar(`매칭 제안 받기가 ${value ? '' : '비'}활성화 되었습니다.`);
      mutate('/api/crews/settings/');
      setIsSubmitting(false);
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  };

  const handleOfferAllow = async (value) => {
    if (value !== data.offer_allow) {
      postOfferAllow(value);
    }
  }

  const handleOptIn = async (value, optInType) => {
    try {
      setIsSubmitting(true);
      const optInData = {
        allow_email: optInType === 'email' ? value : allowEmail,
        allow_sms: optInType === 'sms' ? value : allowSms,
      }
      await axios.post(
        `/api/accounts/opt-in/`,
        optInData
      );
      enqueueSnackbar(`${optInType === 'email' ? '이메일' : '휴대전화'} 정보수신이 ${value ? '' : '비'}활성화 되었습니다.`);
      await getUser();
      setIsSubmitting(false);
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
    }
  }

  return (
    <Grid item xs={12}>
      {!data && (
        <Stack spacing={4}>
          <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
            <CardContent>
              <Skeleton variant="text" height={56} />
            </CardContent>
          </Card>
          <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
            <CardContent>
              <Skeleton variant="text" height={56} />
              <Skeleton variant="text" height={56} />
            </CardContent>
          </Card>
      </Stack>
      )}
      {data && (
        <Stack spacing={4}>
          <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    disabled={isSubmitting || isValidating}
                    checked={offerAllow} onChange={(_, value) => {handleOfferAllow(value)}} />
                }
                labelPlacement="start"
                label={
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      매칭 제안 받기
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      제안 받기를 비활성화 하면 60일간 사이드잡 제안을 드리지 않습니다
                    </Typography>
                    {!data?.offer_allow && data?.disallowed_until && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        비활성화 기한: {data?.disallowed_until} 까지
                      </Typography>
                    )}
                  </>
                }
                sx={{ mx: 0, width: 1, justifyContent: 'space-between' }}
              />
            </CardContent>
          </Card>

          <Card sx={{ border: (theme) => `1px solid ${theme.palette.grey[500_32]}` }}>
            <CardHeader
              title="마케팅 정보수신 동의"
              titleTypographyProps={{ variant: 'subtitle2' }}
            />
            <CardContent sx={{ pt: 2 }}>
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={isSubmitting || isValidating}
                      checked={allowSms} onChange={(_, value) => {handleOptIn(value, 'sms')}} />
                  }
                  labelPlacement="start"
                  label={
                    <Stack direction="row" alignItems="center">
                      <IconStyle icon={'ic:baseline-phone-iphone'} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        휴대전화
                      </Typography>
                    </Stack>
                  }
                  sx={{ mx: 0, width: 1, justifyContent: 'space-between' }}
                />

                <Divider />

                <FormControlLabel
                  control={
                    <Switch
                      disabled={isSubmitting || isValidating}
                      checked={allowEmail} onChange={(_, value) => {handleOptIn(value, 'email')}} />
                  }
                  labelPlacement="start"
                  label={
                    <Stack direction="row" alignItems="center">
                      <IconStyle icon={'ic:baseline-email'} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        이메일
                      </Typography>
                    </Stack>
                  }
                  sx={{ mx: 0, width: 1, justifyContent: 'space-between' }}
                />

              </Stack>
            </CardContent>
          </Card>

        </Stack>
      )}
    </Grid>
  )
}
